import React from "react";
import { Link } from "react-router-dom";
import { Quote, ArrowRight, User } from "lucide-react";
import { SectionLabel } from "../components/SectionHeading";
import { useSite, absoluteUrl } from "../context/SiteContext";
import SmartImage from "../components/SmartImage";

export default function LeadershipPage() {
  const { leaders } = useSite();

  return (
    <div data-testid="leadership-page">
      {/* Header */}
      <section className="border-b border-[#1F3550] relative">
        <div className="absolute inset-0 blueprint-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <SectionLabel code="L/00">Leadership</SectionLabel>
          <h1 className="mt-6 font-heading font-black text-4xl md:text-6xl uppercase tracking-tight leading-[0.95] max-w-4xl">
            The people behind <span className="text-[#F39C12]">every commitment</span>.
          </h1>
          <p className="mt-6 text-lg text-[#94A3B8] max-w-3xl leading-relaxed">
            TST & MK Group's leadership has built the company on two decades of hands-on
            industrial experience, engineering precision and an obsessive focus on the
            promises we sign with our clients.
          </p>
        </div>
      </section>

      {/* Leaders */}
      <section>
        {leaders.map((l, idx) => {
          const reverse = idx % 2 === 1;
          return (
            <div
              key={l.id}
              data-testid={`leader-${l.id}`}
              className={`border-b border-[#1F3550] ${idx % 2 === 0 ? "bg-[#050A0F]" : "bg-[#0D1B2A]"}`}
            >
              <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                {/* Photo */}
                <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                  <div className="relative border border-[#1F3550] aspect-[4/5] bg-[#0D1B2A] overflow-hidden">
                    {l.photo ? (
                      <SmartImage src={l.photo} alt={l.name} imgClassName="grayscale hover:grayscale-0 transition-all duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="h-32 w-32 text-[#1F3550]" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-[#050A0F]/90 p-5 border-t border-[#F39C12]">
                      <div className="font-mono text-[10px] tracking-[0.3em] text-[#F39C12]">L/0{idx + 1}</div>
                      <div className="mt-1 font-heading font-bold text-[#F8FAFC]">{l.name}</div>
                    </div>
                  </div>
                </div>

                {/* Bio + quote */}
                <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F39C12]">
                    {l.title}
                  </div>
                  <h2 className="mt-3 font-heading font-black text-3xl md:text-4xl uppercase tracking-tight leading-tight">
                    {l.name}
                  </h2>
                  {l.bio && (
                    <p className="mt-6 text-[#94A3B8] text-base md:text-lg leading-relaxed whitespace-pre-line">
                      {l.bio}
                    </p>
                  )}
                  {l.quote && (
                    <blockquote className="mt-8 border-l-2 border-[#F39C12] pl-6 py-2">
                      <Quote className="h-6 w-6 text-[#F39C12]" />
                      <p className="mt-3 text-[#F8FAFC] text-lg italic leading-relaxed">
                        "{l.quote}"
                      </p>
                      <div className="mt-4 font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">
                        — {l.name}, {l.title}
                      </div>
                    </blockquote>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {leaders.length === 0 && (
          <div className="max-w-7xl mx-auto px-6 py-32 text-center text-[#94A3B8]">
            Leadership profiles coming soon.
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="bg-[#F39C12] text-[#050A0F]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase">Get in Touch</div>
            <h3 className="mt-3 font-heading font-bold text-2xl md:text-3xl uppercase">
              Work with the leadership team.
            </h3>
          </div>
          <Link
            to="/contact"
            data-testid="leadership-cta"
            className="inline-flex items-center gap-2 bg-[#050A0F] text-[#F39C12] px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#0D1B2A] transition-colors"
          >
            Contact Us <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
